<template>
    <div>
        <PromoProjectSelect
            v-if="!isProjectsLoading && showProjectSelect"
            :eyebrow="projectSelectCopy.eyebrow"
            :title="projectSelectCopy.title"
            :text="projectSelectCopy.text"
            :continue-label="projectSelectContinue"
            show-cancel
            cancel-label="Назад к проектам"
            @select="onProjectSelected"
            @cancel="cancelProjectSelect"
            :allowed-projects="projectSelectAllowed"
            :disable-onboarding-projects="Boolean(marketplaceIntent)"
        />
        <div v-else class="content-wrapper">
            <Tour :steps="steps"></Tour>
            <div class="content">
            <!--Диалоговые окна-->
            <v-dialog v-model="isAddUserDialog" width="500">
                <AddUserDialog :projectId="currentProjectId"> </AddUserDialog>
            </v-dialog>
            <ChangeProjectImageDialog :projectId="currentProjectId"> </ChangeProjectImageDialog>
            <!---->
            <div class="projects-page-header">
                <div class="projects-page-title">
                    <h1>Ваши проекты</h1>
                    <HintIcon hintId="projects" :size="18" id="tour-1" @click.native="finishTour"></HintIcon>
                </div>
                <div
                    v-if="!isProjectsLoading && projects.length > 6"
                    class="projects-search"
                >
                    <svg class="projects-search__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.8" />
                        <path d="M16 16l4.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                    </svg>
                    <input
                        v-model="searchInput"
                        class="projects-search__input"
                        type="search"
                        autocomplete="off"
                        spellcheck="false"
                        inputmode="search"
                        placeholder="Поиск по названию или ID"
                        @keydown.esc.prevent="clearSearch"
                    />
                    <button
                        v-if="searchInput"
                        type="button"
                        class="projects-search__clear"
                        aria-label="Очистить поиск"
                        @click="clearSearch"
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M7 7l10 10M17 7L7 17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                        </svg>
                    </button>
                </div>
                <button type="button" class="projects-create-btn" @click="goToCreateProject">
                    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path
                            d="M7 1v12M1 7h12"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                        />
                    </svg>
                    Создать
                </button>
            </div>
            <div>
                <v-scroll-x-transition :hide-on-leave="true">
                    <v-row v-if="isProjectsLoading" justify="center" align="center">
                        <v-progress-circular class="mt-16" indeterminate color="mainNew"></v-progress-circular>
                    </v-row>
                    <div v-else>
                        <ProjectsPrimaryBanner
                            v-if="!isSuperUser"
                            :projects="projects"
                            @connect="onConnectChannel"
                            @open-marketplace="onOpenMarketplace"
                        ></ProjectsPrimaryBanner>
                        <CardsView
                            :projects="projects"
                            :search-input="searchInput"
                            @clear-search="clearSearch"
                        ></CardsView>
                    </div>
                </v-scroll-x-transition>
            </div>
            </div>
        </div>
        <v-overlay :value="channelConnectBusy" opacity="0.6" z-index="3000">
            <v-progress-circular indeterminate color="mainNew" size="56"></v-progress-circular>
        </v-overlay>
    </div>
</template>

<script>
// @ is an alias to /src
import Tour from '@/components/Tour.vue'
import { mapState } from 'vuex'

import HintIcon from '@/components/HintIcon'
import CardsView from '@/components/project/CardsView'
import ProjectsPrimaryBanner from '@/components/project/ProjectsPrimaryBanner'
import PromoProjectSelect from '@/components/promo/PromoProjectSelect.vue'
import AddUserDialog from '@/components/project/dialogs/AddUserDialog'
import ChangeProjectImageDialog from '@/components/project/dialogs/ChangeProjectImageDialog'
import { mapStores } from 'pinia'
import { useConfirmStore } from '../store/stores/confirm'
import { getChannelFlowProjects, mailingChannelLabel } from '@/helpers/projects/connectMailingChannel'
import { runConnectMailingChannel } from '@/helpers/projects/runConnectMailingChannel'
import cfg from '@root/config'
export default {
    name: 'Projects',
    components: {
        CardsView,
        ProjectsPrimaryBanner,
        PromoProjectSelect,
        AddUserDialog,
        ChangeProjectImageDialog,
        HintIcon,
        Tour,
    },
    data: () => ({
        searchInput: '',
        currentProjectId: null,
        channelConnectType: null,
        marketplaceIntent: null,
        channelConnectBusy: false,
        steps: [
            {
                target: '#tour-1',
                header: {
                    title: 'Get Started',
                },
                content: `Рады видеть вас в числе пользователей сервиса DataHeroes. Если по ходу использования приложения у вас будут возникать вопросы, вы можете обратиться к подсказкам. Они выглядят как знак вопроса, за которым кроется нужная информация`,
                params: {
                    highlight: true,
                    placement: 'right',
                    enableScrolling: false,
                    modifiers: [
                        //from https://popper.js.org/
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 50],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                mainAxis: true,
                                altAxis: true,
                            },
                        },
                    ],
                },
            },
        ],
    }),
    computed: {
        ...mapState(['projects']),
        ...mapState('project', ['isProjectsLoading']),
        ...mapStores(useConfirmStore),
        isAddUserDialog: {
            get() {
                return this.$store.state.project.isAddUserDialog
            },
            set(value) {
                this.$store.commit('project/updateIsAddUserDialog', value)
            },
        },
        showProjectSelect() {
            return Boolean(this.channelConnectType || this.marketplaceIntent)
        },
        projectSelectCopy() {
            if (this.marketplaceIntent) {
                const name = this.marketplaceIntent.name
                return {
                    eyebrow: 'Маркетплейс',
                    title: `Выберите проект, для которого вы хотите открыть «${name}»`,
                    text: `Страница продукта откроется для выбранного проекта.`,
                }
            }
            const name = mailingChannelLabel(this.channelConnectType)
            return {
                eyebrow: 'Подключение канала',
                title: `Выберите проект, для которого вы хотите подключить ${name}`,
                text: `${name} будет подключён к выбранному проекту.`,
            }
        },
        projectSelectContinue() {
            return this.marketplaceIntent ? 'Перейти в маркетплейс' : 'Перейти к подключению'
        },
        channelFlowProjects() {
            return getChannelFlowProjects(this.projects)
        },
        projectSelectAllowed() {
            return this.channelConnectType ? this.channelFlowProjects : null
        },
        isSuperUser() {
            return cfg.adminsWhitelist.includes(this.$auth.user.sub)
        },
        onlyProjectId() {
            const list = this.projects || []
            if (list.length !== 1) return null
            return list[0]?.id || null
        },
    },
    methods: {
        goToCreateProject() {
            this.$router.push({ name: 'OnBoardingCreateProject' })
        },
        clearSearch() {
            this.searchInput = ''
        },

        openAddUserDialog(projectId) {
            this.currentProjectId = projectId
            this.$store.commit('project/updateIsAddUserDialog', true)
        },
        changeProjectImage(projectId) {
            const fileIsPngOrJpg = file => {
                if (!file) {
                    return false
                }
                const type = file.type.split('/')[1]
                return type === 'jpg' || type === 'png' || type === 'jpeg'
            }
            const input = document.createElement('input')
            input.type = 'file'
            input.click()
            input.onchange = async () => {
                const file = input.files[0]
                if (!fileIsPngOrJpg(file)) {
                    this.$store.commit('setError', 'Файл должен быть в формате jpg, jpeg или png', {
                        root: true,
                    })
                    return
                }
                await this.$store.dispatch('project/changeProjectImage', {
                    projectId: projectId,
                    file: file,
                })
            }
        },
        deleteProject(projectId) {
            this.confirmStore.confirm({
                text: `Проект будет удален.`,
                onConfirm: async () => {
                    await this.$store.dispatch('project/deleteProject', projectId)
                },
            })
        },
        renameProject(projectId) {
            this.$store.dispatch('project/callRenameProjectDialog', projectId)
        },
        startTour() {
            const getStartedTourIsFinished = localStorage.getItem('getStartedTourIsFinished')
            if (!getStartedTourIsFinished) {
                this.$tours['getStarted'].start()
            }
        },
        finishTour() {
            this.$tours['getStarted'].stop()
            localStorage.setItem('getStartedTourIsFinished', true)
        },
        onConnectChannel(channelType) {
            if (!channelType) return
            this.marketplaceIntent = null
            const onlyProjectId = this.onlyProjectId
            if (onlyProjectId) {
                this.connectChannelForProject(onlyProjectId, channelType)
                return
            }
            this.channelConnectType = channelType
        },
        onOpenMarketplace(item) {
            if (!item?.integrationId) return
            this.channelConnectType = null
            const integrationId = String(item.integrationId)
            const onlyProjectId = this.onlyProjectId
            if (onlyProjectId) {
                this.openMarketplaceForProject(onlyProjectId, integrationId)
                return
            }
            this.marketplaceIntent = {
                integrationId,
                name: item.title || 'продукт',
            }
        },
        cancelProjectSelect() {
            this.channelConnectType = null
            this.marketplaceIntent = null
        },
        openMarketplaceForProject(projectId, integrationId) {
            this.$router.push(`/project/${projectId}/marketplace/integration/${integrationId}`)
        },
        async connectChannelForProject(projectId, channelType) {
            if (!projectId || !channelType || this.channelConnectBusy) return
            this.channelConnectBusy = true
            try {
                await runConnectMailingChannel({
                    store: this.$store,
                    router: this.$router,
                    projectId,
                    channelType,
                })
            } catch (e) {
                this.$store.dispatch('callNotify', 'Не удалось открыть подключение канала')
            } finally {
                this.channelConnectBusy = false
            }
        },
        async onProjectSelected(projectId) {
            if (!projectId || this.channelConnectBusy) return
            if (this.marketplaceIntent) {
                this.openMarketplaceForProject(projectId, this.marketplaceIntent.integrationId)
                return
            }
            const channelType = this.channelConnectType
            if (!channelType) return
            await this.connectChannelForProject(projectId, channelType)
        },
    },

    async created() {
        this.$on('deleteProject', this.deleteProject)
        this.$on('addUser', this.openAddUserDialog)
        this.$on('renameProject', this.renameProject)
        this.$on('changeProjectImage', this.changeProjectImage)
    },
    mounted() {
        this.startTour()
    },
}
</script>
<style lang="sass" scoped>
@import '../vars.sass'

.projects-page-header
    display: flex
    align-items: center
    gap: 12px 16px
    margin-bottom: 18px
    flex-wrap: wrap

.projects-page-title
    display: flex
    align-items: center
    gap: 9px
    min-width: 0
    order: 1
    flex: 1 1 auto

    h1
        margin: 0
        font-family: $font-family-base !important
        font-size: 23px !important
        font-weight: 900 !important
        letter-spacing: -.03em
        line-height: 1.2 !important
        color: $dark-new !important

.projects-search
    position: relative
    order: 3
    flex: 1 1 100%
    min-width: 0

.projects-search__icon
    position: absolute
    left: 14px
    top: 50%
    width: 18px
    height: 18px
    transform: translateY(-50%)
    color: $muted-new
    pointer-events: none

.projects-search__input
    display: block
    width: 100%
    height: 42px
    box-sizing: border-box
    margin: 0
    border: 1px solid #E6E9F0
    border-radius: 11px
    background: #fff
    padding: 0 40px 0 42px
    font-family: $font-family-base !important
    font-size: 14px !important
    font-weight: 500 !important
    line-height: 42px
    color: $dark-new !important
    outline: none
    box-shadow: none
    transition: border-color .15s, box-shadow .15s
    -webkit-appearance: none

    &::placeholder
        color: $muted-new !important
        font-weight: 500
        opacity: 1

    &:hover
        border-color: $main-new

    &:focus
        border-color: $main-new
        box-shadow: 0 0 0 3px rgba(82, 90, 213, .12)

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration
        display: none

.projects-search__clear
    position: absolute
    right: 8px
    top: 50%
    width: 28px
    height: 28px
    padding: 0
    border: none
    border-radius: 8px
    background: transparent
    color: $muted-new !important
    display: inline-flex
    align-items: center
    justify-content: center
    cursor: pointer
    transform: translateY(-50%)
    transition: background .15s, color .15s

    svg
        width: 16px
        height: 16px

    &:hover
        background: #EFF1F6
        color: $dark-new !important

@media (min-width: 760px)
    .projects-page-title
        flex: 0 1 auto

    .projects-search
        order: 2
        flex: 1 1 240px
        max-width: none

    .projects-create-btn
        order: 3

.projects-create-btn
    display: inline-flex
    align-items: center
    justify-content: center
    gap: 8px
    margin: 0
    border: none
    border-radius: 11px
    padding: 12px 18px
    order: 2
    background: $main-new
    color: #fff !important
    font-family: $font-family-base !important
    font-weight: 800 !important
    font-size: 11.5px !important
    letter-spacing: .05em
    text-transform: uppercase
    line-height: 1
    cursor: pointer
    box-shadow: 0 3px 14px rgba(82, 90, 213, .28)
    transition: background .15s, transform .15s, filter .15s
    flex-shrink: 0
    margin-left: auto

    svg
        width: 14px
        height: 14px
        flex-shrink: 0

    &:hover
        background: #454CC0
        transform: translateY(-1px)

    &:active
        transform: translateY(0)

    &:focus-visible
        outline: 2px solid rgba($main-new, .45)
        outline-offset: 2px
</style>
